import { MapMarker } from '@wehunt/map'
import { visibleGamePins } from '../../lib/replayEvents'
import type {
  ReplayBundle,
  ReplayEventFilterKey,
  ReplayGameKind,
  ReplayGamePin,
} from '../../lib/replayTypes'
import { replayPinSrc } from './replayIcons'
import { ReplayNameChip } from './ReplayNameChip'

export interface ReplayGamePinLayerProps {
  bundle: ReplayBundle
  cursor: number
  hiddenEvents: ReadonlySet<ReplayEventFilterKey>
}

/**
 * Game pins pop in when playback reaches their moment and stay for the rest of the
 * hunt, so the count on the map matches the count in the report.
 */
export function ReplayGamePinLayer({ bundle, cursor, hiddenEvents }: ReplayGamePinLayerProps) {
  const pins = visibleGamePins(bundle, cursor, hiddenEvents)

  return (
    <div data-testid="replay-game-pin-layer">
      {pins.map((pin) => (
        <GamePin key={pin.id} pin={pin} />
      ))}
    </div>
  )
}

/** Wounded game shares the downed artwork — the archive's own two-icon set. */
function pinKindSrc(kind: ReplayGameKind): string {
  return replayPinSrc(kind === 'observed' ? 'observed' : 'downed')
}

function GamePin({ pin }: { pin: ReplayGamePin }) {
  const label = pin.gameNames.join(', ')

  // Anchored at the bottom centre: the old DOM math positioned this content with
  // `left/top` at the point and `transform: translate(-50%, -100%)` — shifting it
  // left by half its own width and up by its full height, so its bottom-centre
  // (not its middle) lands on the point. That is exactly `MapMarker`'s `anchor:
  // "bottom"` — a pin points at its place, it does not sit centred on it.
  //
  // zIndex 2: above the trail `<canvas>`, which `HuntReplayViewer` renders as a DOM
  // sibling after `MapView` and which therefore paints as a z-auto element — without
  // an explicit z-index here a game pin painted before the canvas in DOM order would
  // sit underneath it, and trails overdraw downed-game pins exactly where they
  // converge. 2 keeps this below `Marker`'s 3 and `BarkingCard`'s 4, the same
  // relative order (trails < game pins < participant markers < barking) the old
  // flat-DOM layout gave these four layers.
  return (
    <MapMarker lat={pin.lat} lon={pin.lon} anchor="bottom" zIndex={2}>
      <div
        data-testid={`replay-game-pin-${pin.id}`}
        data-kind={pin.kind}
        // `wh-replay-pop` used to repeat this element's own bottom-anchor translate
        // in its keyframes so the pin didn't jump to a different anchor mid-animation.
        // `MapMarker` now owns that translate (via `anchor="bottom"`) on the portal
        // element this div is nested inside, so the keyframes animate scale/opacity
        // only — repeating the translate here would double-apply it.
        className="wh-replay-pop"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {label !== '' && <ReplayNameChip>{label}</ReplayNameChip>}
        <img
          src={pinKindSrc(pin.kind)}
          alt=""
          width={30}
          height={31}
          draggable={false}
          style={{ display: 'block' }}
        />
      </div>
    </MapMarker>
  )
}
